import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'wallweek_token';
const USER_KEY = 'wallweek_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); }
  catch { return null; }
};

/** True only when the user is admin AND has not switched to "vedi come utente normale". */
export const isAdminView = () => {
  const u = getStoredUser();
  if (!u?.is_admin || window.location.pathname.startsWith('/simulazione/')) return false;
  return !localStorage.getItem('wallweek_view_as_normal');
};
export const setAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !window.location.pathname.startsWith('/simulazione/')) {
      clearAuth();
      if (window.location.pathname !== '/auth') {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(err);
  }
);

// ---- Auth ----

export const register = async ({ email, password, googleToken, invitedBy, name, town, accountType = 'private', companyName, vatNumber, fiscalCode, billingAddress, interests, businessCategory }) => {
  const { data } = await api.post('/auth/register', {
    email, password: googleToken ? null : password, google_token: googleToken || null, invited_by: invitedBy || null, name, town,
    account_type: accountType,
    company_name: companyName || null,
    vat_number: vatNumber || null,
    fiscal_code: fiscalCode || null,
    billing_address: billingAddress || null,
    business_category: accountType === 'business' ? (businessCategory || null) : null,
    interests: Array.isArray(interests) ? interests : null,
    accepted_terms: true,
    accepted_terms_at: new Date().toISOString(),
  });
  setAuth(data.token, { id: data.user_id, email: data.email, name: data.name, account_type: data.account_type });
  return data;
};

export const login = async ({ email, password }) => {
  const { data } = await api.post('/auth/login', { email, password });
  setAuth(data.token, { id: data.user_id, email: data.email, name: data.name, account_type: data.account_type, is_admin: !!data.is_admin });
  return data;
};

export const logout = () => {
  clearAuth();
};

export const deleteMyAccount = async () => {
  const { data } = await api.delete('/users/me');
  clearAuth();
  return data;
};

export const getUserId = () => getStoredUser()?.id;

// ---- Activities ----

export const listActivities = async ({ town = '', category = 'all', date = '', author = '', q = '', premiumOnly = false } = {}) => {
  const params = {};
  if (town) params.town = town;
  if (category && category !== 'all') params.category = category;
  if (date) params.date = date;
  if (author) params.author = author;
  if (q) params.q = q;
  if (premiumOnly) params.premium_only = true;
  const { data } = await api.get('/activities', { params });
  return data;
};

// Autosuggest for the Explore search bar. Returns up to `limit` existing
// public, future event titles that match the user's partial query.
export const suggestActivityTitles = async (q, limit = 8) => {
  if (!q || q.trim().length < 2) return [];
  try {
    const { data } = await api.get('/activities/suggest', { params: { q: q.trim(), limit } });
    return Array.isArray(data?.suggestions) ? data.suggestions : [];
  } catch {
    return [];
  }
};

export const getTrending = async ({ location = '', limit = 5 } = {}) => {
  const params = { limit };
  if (location) params.location = location;
  const { data } = await api.get('/trending', { params });
  return data;
};

export const getMyBadges = async () => {
  const { data } = await api.get('/me/badges');
  return data;
};

export const getActivity = async (id, opts = {}) => {
  const params = {};
  if (opts.invito) params.invito = opts.invito;
  const { data } = await api.get(`/activities/${id}`, { params });
  return data;
};

export const regenerateInviteCode = async (activityId) => {
  const { data } = await api.post(`/activities/${activityId}/invite-code`);
  return data;
};

export const createActivity = async (payload) => {
  const body = {
    title: payload.title,
    location: payload.location,
    place: payload.place || null,
    description: payload.description,
    category: payload.category,
    subcategory: payload.subcategory || null,
    date: payload.date || null,
    time: payload.time || null,
    cost_per_person: payload.costPerPerson != null && payload.costPerPerson !== '' ? Number(payload.costPerPerson) : null,
    phone: payload.phone || null,
    website: payload.website || null,
    max_participants: payload.maxParticipants != null && payload.maxParticipants !== '' ? Number(payload.maxParticipants) : null,
    type: payload.type,
    color: payload.color,
    pin_color: payload.pinColor,
    image: payload.image || null,
    image_focus: payload.imageFocus || null,
    video: payload.video || null,
    rotation: payload.rotation ?? 0,
    is_group: !!payload.isGroup,
    boosted: !!payload.boosted,
    visibility: payload.visibility || 'public',
    invited_user_ids: payload.invitedUserIds || [],
    invited_emails: payload.invitedEmails || [],
    show_on_wall: !!payload.showOnWall,
    offer_items: payload.offerItems || null,
    offer_pdf: payload.offerPdf || null,
    end_time: payload.endTime || null,
    series_dates: payload.seriesDates?.length > 1 ? payload.seriesDates : null,
    offer_pdf_name: payload.offerPdf ? payload.offerPdfName || null : null,
    offer_pdf_size: payload.offerPdf ? payload.offerPdfSize || null : null,
  };
  const { data } = await api.post('/activities', body);
  return data;
};

export const updateActivity = async (id, payload, scope = null) => {
  const body = {};
  const map = {
    title: 'title', location: 'location', place: 'place', description: 'description',
    category: 'category', subcategory: 'subcategory', date: 'date', time: 'time',
    costPerPerson: 'cost_per_person', phone: 'phone', website: 'website',
    maxParticipants: 'max_participants',
    type: 'type', color: 'color', pinColor: 'pin_color', image: 'image', imageFocus: 'image_focus', video: 'video',
    posterImage: 'poster_image', posterImageFocus: 'poster_image_focus',
    showOnWall: 'show_on_wall', offerItems: 'offer_items',
    offerPdf: 'offer_pdf', offerPdfName: 'offer_pdf_name', offerPdfSize: 'offer_pdf_size', endTime: 'end_time',
  };
  Object.entries(map).forEach(([k, v]) => {
    if (payload[k] !== undefined) {
      body[v] = payload[k] === '' && k !== 'offerPdf' && k !== 'endTime' ? null : ((k === 'costPerPerson' || k === 'maxParticipants') ? Number(payload[k]) : payload[k]);
    }
  });
  const { data } = await api.put(`/activities/${id}${scope ? `?scope=${scope}` : ''}`, body);
  return data;
};

export const toggleInterested = async (activityId) => {
  const { data } = await api.post(`/activities/${activityId}/interested`);
  return data;
};

export const toggleFavorite = async (activityId) => {
  const { data } = await api.post(`/activities/${activityId}/favorite`);
  return data;
};

export const getParticipants = async (activityId) => {
  const { data } = await api.get(`/activities/${activityId}/participants`);
  return { ...data, items: (data?.items || []).map((p) => ({ ...p, avatar: resolveMediaSrc(p.avatar) })) };
};

// ---- Invitations (private events) ----

export const searchUsers = async (q) => {
  const { data } = await api.get('/users/search', { params: { q } });
  return (data.items || []).map((u) => ({ ...u, avatar: resolveMediaSrc(u.avatar) }));
};

export const inviteToActivity = async (activityId, { userIds = [], emails = [] } = {}) => {
  const { data } = await api.post(`/activities/${activityId}/invite`, {
    user_ids: userIds,
    emails,
  });
  return data;
};

export const removeInvite = async (activityId, invitee) => {
  const { data } = await api.delete(`/activities/${activityId}/invite`, { params: { invitee } });
  return data;
};

export const listInvites = async (activityId) => {
  const { data } = await api.get(`/activities/${activityId}/invites`);
  return { ...data, users: (data?.users || []).map((u) => ({ ...u, avatar: resolveMediaSrc(u.avatar) })) };
};

export const getMyInvites = async () => {
  const { data } = await api.get('/wall/invites');
  return data.items || [];
};

// ---- Vicinato (Neighborhood) ----

export const addNeighbor = async (userId) => {
  const { data } = await api.post(`/users/${userId}/neighbor`);
  return data;
};

export const removeNeighbor = async (userId) => {
  const { data } = await api.delete(`/users/${userId}/neighbor`);
  return data;
};

export const listNeighbors = async (ownerId) => {
  const { data } = await api.get(`/users/${ownerId}/neighbors`);
  return { ...data, items: (data?.items || []).map((n) => ({ ...n, avatar: resolveMediaSrc(n.avatar) })) };
};

// ---- Direct Messages (1-on-1 chat) ----

export const listDmThreads = async () => {
  const { data } = await api.get('/dm');
  return data.items || [];
};

export const getDmThread = async (otherId, { after = null } = {}) => {
  const params = after ? { after } : undefined;
  const { data } = await api.get(`/dm/${otherId}`, { params });
  return data;
};

export const sendDm = async (otherId, { text = '', image = null, video = null, audio = null, file = null } = {}) => {
  const { data } = await api.post(`/dm/${otherId}`, { text, image, video, audio, file });
  return data;
};

export const markDmRead = async (otherId) => {
  const { data } = await api.post(`/dm/${otherId}/read`);
  return data;
};

// ---- Event Group Chat (auto for participants) ----

export const getEventChat = async (activityId, params) => {
  const { data } = await api.get(`/activities/${activityId}/chat`, params ? { params } : undefined);
  return data;
};

export const sendEventChat = async (activityId, { text = '', image = null, video = null, audio = null, file = null } = {}) => {
  const { data } = await api.post(`/activities/${activityId}/chat`, { text, image, video, audio, file });
  return data;
};

/**
 * Upload a large media asset (video, big doc) via the Object Storage backend endpoint.
 * Uses XHR so we can report real upload progress (fetch has no upload-progress event).
 * Returns { ref, path, size, content_type, original_filename }.
 *   ref = "storage://<path>" — the string to put into a chat message field.
 * onProgress receives a 0..100 integer percentage.
 */
export const uploadChatMedia = (file, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API}/upload/chat-media`);
  const token = getToken();
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.upload.onprogress = (e) => {
    if (e.lengthComputable && onProgress) {
      onProgress(Math.round((e.loaded / e.total) * 100));
    }
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { reject(new Error('Risposta server non valida')); }
    } else {
      let detail = 'Upload fallito';
      try { detail = JSON.parse(xhr.responseText).detail || detail; } catch {}
      reject(new Error(detail));
    }
  };
  xhr.onerror = () => reject(new Error('Errore di rete durante upload'));
  xhr.onabort = () => reject(new Error('Upload annullato'));
  const form = new FormData();
  form.append('file', file);
  xhr.send(form);
  // Return an "abort" hook via the returned promise's .abort — we attach it to a
  // detached property so callers can cancel long uploads.
  xhr.abortRef = () => { try { xhr.abort(); } catch {} };
  // Attach to promise via callback if consumer needs it (see resolveMediaSrc consumers).
  if (typeof onProgress === 'function' && onProgress._registerAbort) {
    onProgress._registerAbort(xhr.abortRef);
  }
});

/**
 * Convert a base64 data URL (from FileReader.readAsDataURL, used for instant
 * local preview) into a small 'storage://<path>' reference by uploading the
 * decoded bytes to object storage. Non-data-URL values (already a storage://
 * ref, an http(s) URL, or empty) are returned unchanged — safe to call
 * unconditionally before saving an activity/profile.
 */
export const uploadMediaIfNeeded = async (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return dataUrl;
  }
  const res = await fetch(dataUrl);
  const blob = await res.blob();

  // Normalize photos coming from an iPhone camera roll:
  //  1) honour EXIF orientation (otherwise portrait pics appear rotated
  //     when rendered via <img> on non-WebKit browsers / in some CSS contexts),
  //  2) transcode HEIC/HEIF to JPEG so every client can decode the image.
  // Fallback silently to the raw blob if createImageBitmap isn't supported.
  let normalized = blob;
  if (
    typeof document !== 'undefined' &&
    typeof createImageBitmap === 'function' &&
    (blob.type || '').startsWith('image/')
  ) {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
      const t = blob.type.toLowerCase();
      const outType = (t === 'image/heic' || t === 'image/heif') ? 'image/jpeg' : blob.type;
      const outBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, outType, 0.92)
      );
      if (outBlob) normalized = outBlob;
    } catch { /* keep raw blob */ }
  }

  // iOS Safari WebKit regression (bug #319985): disk-backed File objects
  // created via `new File([blob], ...)` get silently truncated to
  // Content-Length: 0 when uploaded via FormData while a service worker
  // controls the page. Re-wrapping the bytes in an in-memory Blob via
  // arrayBuffer() bypasses the bug and costs ~10-20ms for typical photos.
  const buffer = await normalized.arrayBuffer();
  const inMemory = new Blob([buffer], { type: normalized.type || 'application/octet-stream' });
  const ext = ((normalized.type || '').split('/')[1] || 'bin').split('+')[0];
  const file = new File([inMemory], `upload.${ext}`, { type: inMemory.type });
  const { ref } = await uploadChatMedia(file);
  return ref;
};

/**
 * Resolve a media reference for playback. If it starts with "storage://",
 * builds a backend URL with ?auth=<token> (so <video src> / <a href> can
 * bypass the missing Authorization header). Otherwise returns as-is.
 */
export const resolveMediaSrc = (ref, { width } = {}) => {
  if (!ref || typeof ref !== 'string') return ref;
  if (ref.startsWith('storage://')) {
    const path = ref.slice('storage://'.length);
    const token = getToken() || mediaAuth;
    const params = new URLSearchParams();
    if (token) params.set('auth', token);
    if (width) params.set('w', String(width));
    const qs = params.toString();
    return `${API}/media/${path}${qs ? `?${qs}` : ''}`;
  }
  return ref;
};

export const getPublicUser = async (userId) => {
  const { data } = await api.get(`/users/${userId}`);
  return { ...data, avatar: resolveMediaSrc(data?.avatar) };
};

export const boostActivity = async (activityId, paymentId, tier = 'gold', method = 'paypal', boostStartDate = null, boostDays = null, boostDates = null, tokenIds = null) => {
  const body = {
    payment_id: paymentId,
    payment_method: method,
    tier,
  };
  if (boostStartDate) body.boost_start_date = boostStartDate;
  if (boostDays) body.boost_days = boostDays;
  if (Array.isArray(boostDates) && boostDates.length > 0) body.boost_dates = boostDates;
  if (Array.isArray(tokenIds) && tokenIds.length > 0) body.token_ids = tokenIds;
  const { data } = await api.post(`/activities/${activityId}/boost`, body);
  return data;
};

export const getBoostAvailability = async ({ location = '', date = '' } = {}) => {
  const params = {};
  if (location) params.location = location;
  if (date) params.date = date;
  const { data } = await api.get('/boost/availability', { params });
  return data;
};

export const deleteActivity = async (id, scope = null) => {
  const { data } = await api.delete(`/activities/${id}${scope ? `?scope=${scope}` : ''}`);
  return data;
};

// ---- Wall ----

export const getMyWall = async () => {
  const { data } = await api.get('/wall');
  return data;
};

export const getSavedIds = async () => {
  const { data } = await api.get('/wall/saved-ids');
  return data.ids || [];
};

export const saveToWall = async (activityId) => {
  const { data } = await api.post(`/wall/save/${activityId}`);
  return data;
};

export const removeFromWall = async (activityId) => {
  const { data } = await api.delete(`/wall/save/${activityId}`);
  return data;
};

// ---- Comments ----

export const listComments = async (activityId) => {
  const { data } = await api.get(`/activities/${activityId}/comments`);
  return data;
};

export const addComment = async (activityId, text) => {
  const { data } = await api.post(`/activities/${activityId}/comments`, { text });
  return data;
};

export const deleteComment = async (activityId, commentId) => {
  const { data } = await api.delete(`/activities/${activityId}/comments/${commentId}`);
  return data;
};

// ---- Password recovery ----

export const forgotPassword = async (email) => {
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
};

export const resetPassword = async (token, newPassword) => {
  const { data } = await api.post('/auth/reset-password', { token, new_password: newPassword });
  return data;
};

// ---- Notifications & moderation ----

export const getNotifications = async () => {
  const { data } = await api.get('/notifications');
  return data;
};

export const markNotificationsRead = async () => {
  const { data } = await api.post('/notifications/mark-read');
  return data;
};

export const deleteAdminNotification = async (notifId) => {
  const { data } = await api.delete(`/notifications/admin/${notifId}`);
  return data;
};

export const dismissActivityNotification = async (activityId) => {
  const { data } = await api.delete(`/notifications/activity/${activityId}`);
  return data;
};

export const reportActivity = async (activityId, reason) => {
  const { data } = await api.post(`/activities/${activityId}/report`, { reason });
  return data;
};

export const listReports = async (status = null) => {
  const params = status ? { status } : {};
  const { data } = await api.get('/reports', { params });
  return data;
};

export const deleteReport = async (id) => {
  const { data } = await api.delete(`/reports/${id}`);
  return data;
};

export const resolveReport = async (reportId) => {
  const { data } = await api.post(`/reports/${reportId}/resolve`);
  return data;
};

export const adminReportAction = async (reportId, action, message = null) => {
  const body = { action };
  if (message) body.message = message;
  const { data } = await api.post(`/reports/${reportId}/action`, body);
  return data;
};

export const adminActivityAction = async (activityId, action, message = null) => {
  const body = { action };
  if (message) body.message = message;
  const { data } = await api.post(`/admin/activities/${activityId}/action`, body);
  return data;
};

// ---- Settings ----

// Public settings (categories, boost tiers, feature flags) rarely
// change. We stash them in localStorage with a 10-minute TTL so cold
// starts from mobile skip one of the ~15 cold-start API calls and the
// Home can paint using cached data while the fresh fetch returns in
// the background.
const PUBLIC_SETTINGS_KEY = 'ww:settings:public';
const PUBLIC_SETTINGS_TTL_MS = 10 * 60 * 1000;
// In-flight promise so concurrent callers share a single network call.
// Without this, Gate + useCategories + Home + BoostModal all fire the
// same request in parallel on first paint (3-5x /settings/public).
let _publicSettingsInflight = null;

export const getPublicSettings = async () => {
  // Try fresh fetch; if cached and still valid, return cached immediately
  // and refresh in the background.
  try {
    const raw = localStorage.getItem(PUBLIC_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.ts && Date.now() - parsed.ts < PUBLIC_SETTINGS_TTL_MS && parsed.data) {
        // Fire-and-forget background revalidation so stale data heals,
        // but share one inflight promise across callers.
        if (!_publicSettingsInflight) {
          _publicSettingsInflight = api.get('/settings/public')
            .then(({ data }) => {
              try { localStorage.setItem(PUBLIC_SETTINGS_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
              return data;
            })
            .catch(() => parsed.data)
            .finally(() => { _publicSettingsInflight = null; });
        }
        return parsed.data;
      }
    }
  } catch { /* ignore cache failures */ }
  // No valid cache → fetch, but dedupe concurrent callers.
  if (!_publicSettingsInflight) {
    _publicSettingsInflight = api.get('/settings/public')
      .then(({ data }) => {
        try { localStorage.setItem(PUBLIC_SETTINGS_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
        return data;
      })
      .finally(() => { _publicSettingsInflight = null; });
  }
  return _publicSettingsInflight;
};

export const adminGetSettings = async () => {
  const { data } = await api.get('/admin/settings');
  return data;
};

export const adminUpdateBoostTiers = async (boostTiers) => {
  const { data } = await api.put('/admin/settings/boost', { boost_tiers: boostTiers });
  return data;
};

export const adminUpdatePaymentMethods = async (paymentMethods) => {
  const { data } = await api.put('/admin/settings/payments', { payment_methods: paymentMethods });
  return data;
};

export const adminUpdateCategories = async (categories) => {
  const { data } = await api.put('/admin/settings/categories', { categories });
  return data;
};

// ---- "Lavori in corso" access gate ----

export const adminUpdateNeighborFilter = async (enabled) => (await api.put('/admin/settings/neighbor-filter', { enabled })).data;

export const adminUpdateGate = async ({ gate_enabled, gate_access_code }) => {
  const { data } = await api.put('/admin/settings/gate', {
    gate_enabled: !!gate_enabled,
    gate_access_code: gate_access_code || '',
  });
  return data;
};

// Public endpoint — no auth. The server validates the code and never
// echoes the stored value back to the frontend.
export const verifyGateCode = async (code) => {
  const { data } = await api.post('/gate/verify', { code: code || '' });
  return data;
};

export const adminAddBoostTier = async (payload) => {
  const { data } = await api.post('/admin/settings/boost/tier', payload);
  return data;
};

export const adminDeleteBoostTier = async (tierId) => {
  const { data } = await api.delete(`/admin/settings/boost/tier/${tierId}`);
  return data;
};

export const adminGiftBoost = async ({ mode, userId, activityId, tier, durationDays, note, quantity }) => {
  const { data } = await api.post('/admin/gift-boost', {
    mode: mode || 'token',
    user_id: userId || null,
    activity_id: activityId || null,
    tier,
    duration_days: durationDays || null,
    note: note || null,
    quantity: quantity || 1,
  });
  return data;
};

export const reserveSlot = async ({ location, date }) => {
  const { data } = await api.post('/activities/reserve', { location, date });
  return data;
};

export const listMyReservations = async () => {
  const { data } = await api.get('/me/reservations');
  return data?.reservations || [];
};

export const listMyBoostTokens = async () => {
  const { data } = await api.get('/me/boost-tokens');
  return data;
};

// Owner-only monitoring dashboard.
export const getAdminMonitor = async () => {
  const { data } = await api.get('/admin/monitor');
  return data;
};

// Owner-only breakdown of registered users per Comune.
export const getAdminMonitorTowns = async () => {
  const { data } = await api.get('/admin/monitor/towns');
  return data;
};

export const getAdminTownUsers = async (town) => {
  const { data } = await api.get(`/admin/towns/${encodeURIComponent(town)}/users`);
  return { ...data, users: (data?.users || []).map((u) => ({ ...u, avatar: resolveMediaSrc(u.avatar) })) };
};

export const searchDirectory = async ({ q = '', town = '', limit = 20 } = {}) => {
  const { data } = await api.get('/directory/search', { params: { q, town, limit } });
  return { ...data, users: (data?.users || []).map((u) => ({ ...u, avatar: resolveMediaSrc(u.avatar) })) };
};

export const redeemBoostToken = async (tokenId, activityId, boostDates = null) => {
  const body = { activity_id: activityId };
  if (Array.isArray(boostDates) && boostDates.length > 0) body.boost_dates = boostDates;
  const { data } = await api.post(`/me/boost-tokens/${tokenId}/redeem`, body);
  return data;
};

export const purchaseBoostTokens = async ({ tier, quantity, durationDays, paymentId }) => {
  const { data } = await api.post('/me/boost-tokens/purchase', {
    tier,
    quantity: quantity || 1,
    duration_days: durationDays || null,
    payment_id: paymentId,
  });
  return data;
};

export const getBoostTiers = async () => {
  const data = await getPublicSettings();
  return data?.boost_tiers || {};
};

export const getBoostAvailableDates = async ({ location, tier, fromDate, days }) => {
  const { data } = await api.get('/boost/available-dates', {
    params: { location, tier, from_date: fromDate || null, days: days || 30 },
  });
  return data;
};

export const adminSearchUsers = async (q) => {
  const { data } = await api.get('/admin/users/search', { params: { q } });
  return { ...data, users: (data?.users || []).map((u) => ({ ...u, avatar: resolveMediaSrc(u.avatar) })) };
};

export const getBlockedTerms = async () => {
  const { data } = await api.get('/moderation/blocked-terms');
  return data;
};

export const adminUpdateBlockedTerms = async (terms) => {
  const { data } = await api.put('/admin/settings/blocked-terms', { terms });
  return data;
};

export const supportContact = async (subject, message) => {
  const { data } = await api.post('/support/contact', { subject, message });
  return data;
};

export const listSupportTickets = async (status = null) => {
  const params = status ? { status } : {};
  const { data } = await api.get('/support/tickets', { params });
  return data;
};

export const replySupportTicket = async (id, message) => {
  const { data } = await api.post(`/support/tickets/${id}/reply`, { message });
  return data;
};

export const reopenSupportTicket = async (id) => {
  const { data } = await api.post(`/support/tickets/${id}/reopen`);
  return data;
};

export const deleteSupportTicket = async (id) => {
  const { data } = await api.delete(`/support/tickets/${id}`);
  return data;
};

export const promoteAdmin = async (code) => {
  const { data } = await api.post('/auth/promote-admin', { code });
  // Update stored user
  const cur = getStoredUser();
  if (cur) setAuth(getToken(), { ...cur, is_admin: !!data.is_admin });
  return data;
};

// ---- Profile ----

export const getProfile = async () => {
  const { data } = await api.get('/profile');
  return data;
};

export const updateProfile = async (patch) => {
  const { data } = await api.put('/profile', patch);
  return data;
};

// Normalise API activity shape -> UI-friendly camelCase.
// image/video/authorAvatar/participantAvatars are kept as their RAW stored
// value (a storage:// ref for new uploads, or base64/URL for older docs) —
// not resolved here, because Create.jsx round-trips this same shape back
// into the save payload, and resolving would bake a temporary signed URL
// into the saved document. Display components call resolveMediaSrc() on
// these fields themselves at render time.
export const toUi = (a) => {
  if (!a) return a;
  // Front-end safety net (14 Oct 2026): the backend owns the `boosted`
  // flag, but a document can be out of sync — e.g. the owner edited the
  // date so the boost dropped, but the payload still has
  // boosted=true/boost_tier until the next write cycle. Compute a live
  // flag so every card (public wall, My Wall, detail, modals) agrees.
  const today = new Date().toISOString().slice(0, 10);
  const boostedUntil = a.boosted_until ?? a.boostedUntil ?? null;
  const boostDates = a.boost_dates ?? a.boostDates ?? [];
  const boostTier = a.boost_tier ?? a.boostTier;
  const boostUntilOk = !boostedUntil || String(boostedUntil).slice(0, 10) >= today;
  const premiumDateOk =
    boostTier !== 'premium' ||
    boostDates.length === 0 ||
    boostDates.includes(a.date);
  const boostedLive = !!a.boosted && boostUntilOk && premiumDateOk;
  return {
    ...a,
    boosted: boostedLive,
    pinColor: a.pin_color || a.pinColor,
    posterImage: a.poster_image ?? a.posterImage ?? null,
    posterImageFocus: a.poster_image_focus ?? a.posterImageFocus ?? null,
    isGroup: a.is_group ?? a.isGroup,
    costPerPerson: a.cost_per_person ?? a.costPerPerson,
    maxParticipants: a.max_participants ?? a.maxParticipants ?? null,
    interestedCount: a.interested_count ?? a.interestedCount ?? 0,
    interestedUsers: a.interested_users ?? a.interestedUsers ?? [],
    favoritedCount: a.favorited_count ?? a.favoritedCount ?? 0,
    favoritedUsers: a.favorited_users ?? a.favoritedUsers ?? [],
    authorName: a.author_name ?? a.authorName,
    authorAvatar: a.author_avatar ?? a.authorAvatar,
    boostTier: boostedLive ? boostTier : null,
    boostedUntil,
    boostDates,
    visibility: a.visibility || 'public',
    invitedUserIds: a.invited_user_ids ?? a.invitedUserIds ?? [],
    invitedEmails: a.invited_emails ?? a.invitedEmails ?? [],
    inviteCode: a.invite_code ?? a.inviteCode ?? null,
    showOnWall: a.show_on_wall ?? a.showOnWall ?? false,
    offerItems: a.offer_items ?? a.offerItems ?? [],
    offerPdf: a.offer_pdf ?? a.offerPdf ?? null,
    endTime: a.end_time ?? a.endTime ?? null,
    seriesId: a.series_id ?? a.seriesId ?? null,
    seriesLabel: a.series_label ?? a.seriesLabel ?? null,
    seriesCount: a.series_count ?? a.seriesCount ?? null,
    seriesIds: a.series_ids ?? a.seriesIds ?? null,
    offerPdfName: a.offer_pdf_name ?? a.offerPdfName ?? null,
    offerPdfSize: a.offer_pdf_size ?? a.offerPdfSize ?? null,
    _teaser: a.is_teaser ?? a._teaser ?? false,
    isReservation: a.is_reservation ?? a.isReservation ?? false,
    participantAvatars: a.participant_avatars ?? a.participantAvatars ?? null,
    _createdByMe: a.createdByMe ?? a._createdByMe ?? false,
  };
};
export { api };

// ---- Business menus (menù, proposte del giorno, tipo attività) ----
export const getBusinessMenu = async (userId) => (await api.get(`/businesses/${userId}/menu`)).data;
export const setBusinessCategory = async (category) => (await api.put('/me/business-category', { category })).data;
export const saveBusinessMenu = async (meal, payload) => (await api.put(`/me/menu/${meal}`, payload)).data;
export const saveMenuPublic = async (on) => (await api.put('/me/menu-public', { public_menu: on })).data;
export const saveMenuSplit = async (genderSplit) => (await api.put('/me/menu-split', { gender_split: genderSplit })).data;
export const saveMenuSettings = async (switchTime) => (await api.put('/me/menu-settings', { switch_time: switchTime })).data;
export const grantMenuAccess = async (ownerId, code, kind) => (await api.post(`/businesses/${ownerId}/access`, { code, kind })).data;
export const shareSpecialsLink = async () => (await api.post('/me/specials/share')).data;
export const rotateMenuQr = async () => (await api.post('/me/menu/qr-code/rotate')).data;
export const saveMenuLegend = async (items) => (await api.put('/me/menu-legend', { items })).data;
export const setDishSoldOut = async (itemId, soldOut) => (await api.post('/me/menu/sold-out', { item_id: itemId, sold_out: soldOut })).data;
export const saveBusinessSpecials = async (items, kind = 'specials') => (await api.put(`/me/specials?kind=${kind}`, { items })).data;
export const saveBusinessServices = async (services, labels = {}) => (await api.put('/me/services', { services, labels })).data;
export const saveBusinessHours = async (hours) => (await api.put('/me/hours', hours)).data;
export const saveBusinessContacts = async (contacts) => (await api.put('/me/contacts', contacts)).data;

// ---- Simulazioni (admin sales previews) ----
let mediaAuth = null;
export const setMediaAuth = (t) => { mediaAuth = t; };
export const listSimulations = async () => (await api.get('/admin/simulations')).data.items || [];
export const getSimulation = async (id) => (await api.get(`/admin/simulations/${id}`)).data;
export const createSimulation = async (body) => (await api.post('/admin/simulations', body)).data;
export const updateSimulation = async (id, body) => (await api.put(`/admin/simulations/${id}`, body)).data;
export const renewSimulation = async (id) => (await api.post(`/admin/simulations/${id}/renew`)).data;
export const deleteSimulation = async (id) => (await api.delete(`/admin/simulations/${id}`)).data;
export const viewSimulation = async (token) => (await axios.get(`${API}/sim/${token}`)).data;
export const simulationLink = (token) => `${window.location.origin}/simulazione/${token}`;

// "08:30" or "08:30–12:30" when an end time is set.
export const timeRange = (a) => (a?.time ? (a.endTime ? `${a.time}–${a.endTime}` : a.time) : '');
